/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { VNode } from "preact";
import { useState } from "preact/hooks";
import { StatisticBucketRange } from "@gnu-taler/taler-util";
import { useStatisticsReport, useMerchantConfig } from "../api/hooks.js";
import { getMerchantBackendFeatures } from "../api/merchantBackendFeatures.js";
import {
  StatisticsScreen,
  type StatisticsScreenProps,
} from "../screens/StatisticsScreen.js";

export function StatisticsRoute(props: StatisticsScreenProps): VNode {
  // The period control lives on the screen, so the route follows it.
  const [query, setQuery] = useState<{ period: string; count: number }>({
    period: "quarter",
    count: 4,
  });

  const { primaryCurrency, config } = useMerchantConfig();
  const features = getMerchantBackendFeatures(config?.version);
  const {
    report: txReport,
    isLoading,
    error,
    resource: txResource,
  } = useStatisticsReport("transactions", {
    granularity: query.period as StatisticBucketRange,
    count: query.count,
  });
  const { report: funnelReport, resource: funnelResource } =
    useStatisticsReport("sales-funnel", undefined, features.salesFunnel);

  return (
    <StatisticsScreen
      {...props}
      configCurrency={primaryCurrency}
      txReport={txReport}
      isTxLoading={isLoading}
      txError={error}
      showSalesFunnel={features.salesFunnel}
      funnelReport={funnelReport || undefined}
      txResource={txResource}
      funnelResource={funnelResource}
      onPeriodChange={(period, count) => setQuery({ period, count })}
    />
  );
}
